import type { Ref } from "vue";
import type { MaybeRef, MaybeRefObjectMaybeProperty } from "@/typings/refs";
import type { UseToggleOptions } from "@/typings/options";
import getValue from "@/functions/getValue";
import getProperty from "@/functions/getProperty";
/**
 * @function useToggle - composable function to toggle between values,
 * if its needed to undefine the value, use useSetter instead, as this function will take undefined as a false value
 * @param {MaybeRef<Truthy | Falsy>} initialValue - a maybe ref value to modify, if not passed the function initializes in false
 * @param {MaybeRef<UseToggleOptions>} options - options that modify the behavior of the function
 * @return the toggler function, and a value, the value can be the same ref as provided
 */
export default function useToggle<Truthy = true, Falsy = false>(
  initialValue?: MaybeRef<Truthy | Falsy>,
  options?: MaybeRef<UseToggleOptions<Truthy, Falsy>>
): {
  toggle: (targetValue?: Truthy | Falsy) => void;
  value: Ref<Truthy | Falsy>;
} {
  const undefinedTruthy = getProperty(
    options as MaybeRefObjectMaybeProperty<boolean>,
    "undefinedTruthy",
    false
  );
  const undefinedFalsy = getProperty(
    options as MaybeRefObjectMaybeProperty<boolean>,
    "undefinedFalsy",
    false
  );
  /** value to toggle, if obtained as parameter change that, if a new value */

  const truthyValue: Ref<Truthy> = getProperty<Truthy>(
    options as MaybeRefObjectMaybeProperty<Truthy>,
    "truthyValue",
    undefinedTruthy.value
      ? (undefined as unknown as Truthy)
      : (true as unknown as Truthy)
  );
  const falsyValue = getProperty<Falsy>(
    options as MaybeRefObjectMaybeProperty<Falsy>,
    "falsyValue",
    undefinedFalsy.value
      ? (undefined as unknown as Falsy)
      : (false as unknown as Falsy)
  );
  const value = getValue(initialValue, falsyValue.value as Falsy);
  /** function that toggles the flag*/
  const toggle = (targetValue: Truthy | Falsy | undefined = undefined) => {
    if (targetValue == undefined) {
      if (value.value == truthyValue.value) {
        value.value = falsyValue.value as Falsy;
      } else if (value.value == falsyValue.value) {
        value.value = truthyValue.value as Truthy;
      } else {
        value.value = falsyValue.value as Falsy;
      }
    } else {
      value.value = targetValue;
    }
  };

  return { value, toggle };
}
